import { Injectable, inject } from '@angular/core';
import { RideCaptainProfileService } from './ride-captain-profile.service';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  type DocumentData
} from 'firebase/firestore';
import {
  TRAVEL_COLLECTIONS,
  getLocationAuth,
  getLocationFirestore,
  isLocationFirebaseConfigured
} from '../location';
import type {
  TravelBookingRow,
  TravelCustomerRow,
  TravelDashboardStats,
  TravelDriverRow,
  TravelExpenseRow,
  TravelPaymentRow,
  TravelRentalRow,
  TravelServiceRow,
  TravelStorefrontContent,
  TravelVehicleDocumentRow,
  TravelVehicleRateRow,
  TravelVehicleRow
} from '../../megha/business-travels/travel.types';
import type { StorePublicBrand } from '../utils/public-store-brand.util';

export interface FirebasePublicTravelPayload {
  content: TravelStorefrontContent;
  store: {
    store_code?: string;
    store_name?: string;
    store_type?: string;
    created_by?: string | null;
  } | null;
  identity?: StorePublicBrand | null;
  vehicles: TravelVehicleRow[];
  services?: Array<Record<string, unknown>>;
  packages?: unknown[];
  offers?: unknown[];
  contact?: Record<string, unknown>;
}

export interface FirebasePublicActiveRide {
  kind: 'ride' | 'rental';
  id: string;
  status: string;
  vehicle_id?: string | null;
  vehicle_title?: string | null;
  pickup_location?: string | null;
  drop_location?: string | null;
  travel_date?: string | null;
  travel_time?: string | null;
  pickup_date?: string | null;
  return_date?: string | null;
  fare_amount?: number | null;
  estimated_total_inr?: number | null;
  driver_name?: string | null;
  driver_phone?: string | null;
  created_at?: string | null;
}

export interface FirebaseTravelStoreDoc {
  storeCode: string;
  storeName: string;
  ownerUid: string;
  published: boolean;
  content: TravelStorefrontContent;
  logoUrl?: string | null;
  createdAt?: unknown;
  updatedAt?: unknown;
}

function phoneDigits(phone: string): string {
  return phone.replace(/\D/g, '').slice(-10);
}

function newId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `t_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

function asIso(value: unknown): string | undefined {
  if (!value) return undefined;
  if (typeof value === 'string') return value;
  if (typeof value === 'object' && value && 'toDate' in value) {
    try {
      return (value as { toDate: () => Date }).toDate().toISOString();
    } catch {
      return undefined;
    }
  }
  return undefined;
}

@Injectable({ providedIn: 'root' })
export class FirebaseTravelDataService {
  private readonly rideAdmins = inject(RideCaptainProfileService);
  isConfigured(): boolean {
    return isLocationFirebaseConfigured() && !!getLocationFirestore();
  }

  private db() {
    const db = getLocationFirestore();
    if (!db) throw new Error('Firebase travel is not configured.');
    return db;
  }

  async currentUid(): Promise<string | null> {
    const auth = getLocationAuth();
    await auth?.authStateReady();
    return auth?.currentUser?.uid?.trim() || null;
  }

  // --- Stores ---
  async getStore(storeCode: string): Promise<FirebaseTravelStoreDoc | null> {
    const snap = await getDoc(doc(this.db(), TRAVEL_COLLECTIONS.stores, storeCode.trim()));
    if (!snap.exists()) return null;
    return { storeCode: snap.id, ...(snap.data() as Omit<FirebaseTravelStoreDoc, 'storeCode'>) };
  }

  async ensureStoreForOwner(input: {
    storeCode: string;
    storeName?: string;
    ownerUid: string;
  }): Promise<FirebaseTravelStoreDoc> {
    const code = input.storeCode.trim();
    const existing = await this.getStore(code);
    if (existing) {
      if (existing.ownerUid && existing.ownerUid !== input.ownerUid && !(await this.rideAdmins.isCurrentUserRideAdmin())) {
        throw new Error('You do not manage this travel store.');
      }
      return existing;
    }
    const body: FirebaseTravelStoreDoc = {
      storeCode: code,
      storeName: input.storeName?.trim() || code,
      ownerUid: input.ownerUid,
      published: true,
      content: {
        tagline: 'Book a ride in minutes',
        hero_subtitle: 'Car booking & rental'
      }
    };
    await setDoc(doc(this.db(), TRAVEL_COLLECTIONS.stores, code), {
      ...body,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return body;
  }

  async listPublishedStores(max = 400): Promise<FirebaseTravelStoreDoc[]> {
    const col = collection(this.db(), TRAVEL_COLLECTIONS.stores);
    try {
      const snap = await getDocs(query(col, where('published', '==', true), limit(max)));
      return snap.docs.map((row) => ({
        storeCode: row.id,
        ...(row.data() as Omit<FirebaseTravelStoreDoc, 'storeCode'>)
      }));
    } catch {
      const snap = await getDocs(query(col, limit(max)));
      return snap.docs
        .map((row) => ({
          storeCode: row.id,
          ...(row.data() as Omit<FirebaseTravelStoreDoc, 'storeCode'>)
        }))
        .filter((s) => s.published !== false);
    }
  }

  async listManageableStores(): Promise<FirebaseTravelStoreDoc[]> {
    const uid = await this.currentUid();
    if (!uid) throw new Error('Sign in to manage travel businesses.');
    const admin = await this.rideAdmins.isCurrentUserRideAdmin();
    const snap = await getDocs(query(collection(this.db(), TRAVEL_COLLECTIONS.stores),
      ...(admin ? [] : [where('ownerUid', '==', uid)])));
    return snap.docs.map(row => ({ ...(row.data() as FirebaseTravelStoreDoc), storeCode: row.id }))
      .sort((a, b) => (a.storeName || a.storeCode).localeCompare(b.storeName || b.storeCode));
  }

  async getStorefrontContent(storeCode: string): Promise<{ data: TravelStorefrontContent; error: string | null }> {
    try {
      const store = await this.getStore(storeCode);
      return { data: store?.content || {}, error: null };
    } catch (e) {
      return { data: {}, error: e instanceof Error ? e.message : 'Failed to load storefront.' };
    }
  }

  async saveStorefrontContent(
    storeCode: string,
    content: TravelStorefrontContent
  ): Promise<{ error: string | null }> {
    try {
      const code = storeCode.trim();
      await setDoc(
        doc(this.db(), TRAVEL_COLLECTIONS.stores, code),
        { content, updatedAt: serverTimestamp() },
        { merge: true }
      );
      return { error: null };
    } catch (e) {
      return { error: e instanceof Error ? e.message : 'Failed to save storefront.' };
    }
  }

  // --- Generic list/upsert helpers ---
  private async listByStore<T extends { id: string }>(
    collectionName: string,
    storeCode: string,
    _orderField?: string,
    activeOnly = false
  ): Promise<{ data: T[]; error: string | null }> {
    try {
      const col = collection(this.db(), collectionName);
      const snap = await getDocs(
        query(col, where('storeCode', '==', storeCode.trim()),
          ...(activeOnly ? [where('is_active', '==', true)] : []), limit(500))
      );
      return {
        data: snap.docs.map((row) => this.mapStoreRow<T>(row.id, row.data())),
        error: null
      };
    } catch (e) {
      return { data: [], error: e instanceof Error ? e.message : 'List failed.' };
    }
  }

  private mapStoreRow<T extends { id: string }>(id: string, data: DocumentData): T {
    const out: Record<string, unknown> = { ...data, id, store_code: data['storeCode'] || data['store_code'] };
    if (out['createdAt'] && !out['created_at']) out['created_at'] = asIso(out['createdAt']);
    if (out['updatedAt'] && !out['updated_at']) out['updated_at'] = asIso(out['updatedAt']);
    if (out['paidAt'] && !out['paid_at']) out['paid_at'] = asIso(out['paidAt']);
    return out as T;
  }

  private async upsertByStore(
    collectionName: string,
    storeCode: string,
    id: string | undefined,
    payload: Record<string, unknown>
  ): Promise<{ id: string; error: string | null }> {
    try {
      const code = storeCode.trim();
      const body = {
        ...payload,
        storeCode: code,
        updatedAt: serverTimestamp()
      };
      if (id) {
        await setDoc(doc(this.db(), collectionName, id), body, { merge: true });
        return { id, error: null };
      }
      const ref = await addDoc(collection(this.db(), collectionName), {
        ...body,
        createdAt: serverTimestamp()
      });
      return { id: ref.id, error: null };
    } catch (e) {
      return { id: '', error: e instanceof Error ? e.message : 'Save failed.' };
    }
  }

  private async deleteByStore(
    collectionName: string,
    storeCode: string,
    id: string
  ): Promise<{ error: string | null }> {
    try {
      const snap = await getDoc(doc(this.db(), collectionName, id));
      if (snap.exists() && snap.data()['storeCode'] !== storeCode.trim()) {
        return { error: 'Not found for this store.' };
      }
      await deleteDoc(doc(this.db(), collectionName, id));
      return { error: null };
    } catch (e) {
      return { error: e instanceof Error ? e.message : 'Delete failed.' };
    }
  }

  // --- Vehicles ---
  async listVehicles(storeCode: string, activeOnly = false): Promise<{ data: TravelVehicleRow[]; error: string | null }> {
    const res = await this.listByStore<TravelVehicleRow>(TRAVEL_COLLECTIONS.vehicles, storeCode, undefined, activeOnly);
    if (res.error) return res;
    return {
      data: res.data
        .map((v) => this.normalizeVehicle(v as unknown as Record<string, unknown>))
        .sort((a, b) => a.sort_order - b.sort_order || a.title.localeCompare(b.title)),
      error: null
    };
  }

  async upsertVehicle(
    storeCode: string,
    vehicle: Partial<TravelVehicleRow> & { title: string }
  ): Promise<{ data: TravelVehicleRow | null; error: string | null }> {
    const ownerUid = await this.currentUid();
    if (!ownerUid) return { data: null, error: 'Please sign in to save a vehicle.' };
    const payload = {
      ownerUid,
      sort_order: vehicle.sort_order ?? 0,
      title: vehicle.title.trim(),
      short_description: vehicle.short_description?.trim() || null,
      image_url: vehicle.image_url?.trim() || null,
      image_urls: vehicle.image_urls || [],
      vehicle_brand: vehicle.vehicle_brand?.trim() || null,
      vehicle_type: vehicle.vehicle_type?.trim() || null,
      manufacture_year: vehicle.manufacture_year ?? null,
      fuel_type: vehicle.fuel_type?.trim() || null,
      transmission: vehicle.transmission?.trim() || null,
      seating_capacity: vehicle.seating_capacity ?? null,
      daily_price_inr: vehicle.daily_price_inr ?? null,
      supports_booking: vehicle.supports_booking !== false,
      supports_rental: vehicle.supports_rental !== false,
      is_active: vehicle.is_active !== false,
      extra_json: vehicle.extra_json || {}
    };
    const saved = await this.upsertByStore(
      TRAVEL_COLLECTIONS.vehicles,
      storeCode,
      vehicle.id,
      payload
    );
    if (saved.error) return { data: null, error: saved.error };
    return {
      data: this.normalizeVehicle({ ...payload, id: saved.id, store_code: storeCode }),
      error: null
    };
  }

  async deleteVehicle(storeCode: string, vehicleId: string): Promise<{ error: string | null }> {
    return this.deleteByStore(TRAVEL_COLLECTIONS.vehicles, storeCode, vehicleId);
  }

  // --- Rates / documents ---
  async listVehicleRates(
    storeCode: string,
    vehicleId?: string
  ): Promise<{ data: TravelVehicleRateRow[]; error: string | null }> {
    const res = await this.listByStore<TravelVehicleRateRow>(
      TRAVEL_COLLECTIONS.vehicleRates,
      storeCode
    );
    if (res.error) return res;
    const data = vehicleId ? res.data.filter((r) => r.vehicle_id === vehicleId) : res.data;
    return { data, error: null };
  }

  async upsertVehicleRate(
    storeCode: string,
    rate: Partial<TravelVehicleRateRow> & { vehicle_id: string; service_type: string }
  ): Promise<{ error: string | null }> {
    const payload = {
      vehicle_id: rate.vehicle_id,
      service_type: rate.service_type,
      base_price_inr: rate.base_price_inr ?? null,
      per_km_price_inr: rate.per_km_price_inr ?? null,
      minimum_km: rate.minimum_km ?? null,
      driver_charge_inr: rate.driver_charge_inr ?? null,
      night_charge_inr: rate.night_charge_inr ?? null
    };
    const saved = await this.upsertByStore(
      TRAVEL_COLLECTIONS.vehicleRates,
      storeCode,
      rate.id,
      payload
    );
    return { error: saved.error };
  }

  async deleteVehicleRate(storeCode: string, rateId: string): Promise<{ error: string | null }> {
    return this.deleteByStore(TRAVEL_COLLECTIONS.vehicleRates, storeCode, rateId);
  }

  async listVehicleDocuments(
    storeCode: string,
    vehicleId?: string
  ): Promise<{ data: TravelVehicleDocumentRow[]; error: string | null }> {
    const res = await this.listByStore<TravelVehicleDocumentRow>(
      TRAVEL_COLLECTIONS.vehicleDocuments,
      storeCode
    );
    if (res.error) return res;
    const data = vehicleId ? res.data.filter((d) => d.vehicle_id === vehicleId) : res.data;
    return { data, error: null };
  }

  async upsertVehicleDocument(
    storeCode: string,
    docRow: Partial<TravelVehicleDocumentRow> & {
      vehicle_id: string;
      document_type: string;
      document_url: string;
    }
  ): Promise<{ error: string | null }> {
    const payload = {
      vehicle_id: docRow.vehicle_id,
      document_type: docRow.document_type,
      document_url: docRow.document_url.trim(),
      expiry_date: docRow.expiry_date || null
    };
    const saved = await this.upsertByStore(
      TRAVEL_COLLECTIONS.vehicleDocuments,
      storeCode,
      docRow.id,
      payload
    );
    return { error: saved.error };
  }

  // --- Services ---
  async listServices(storeCode: string): Promise<{ data: TravelServiceRow[]; error: string | null }> {
    return this.listByStore<TravelServiceRow>(TRAVEL_COLLECTIONS.services, storeCode);
  }

  async upsertService(
    storeCode: string,
    svc: Partial<TravelServiceRow> & { name: string; service_type: string }
  ): Promise<{ error: string | null }> {
    const payload = {
      name: svc.name.trim(),
      description: svc.description?.trim() || null,
      service_type: svc.service_type,
      icon: svc.icon?.trim() || null,
      is_active: svc.is_active !== false,
      sort_order: svc.sort_order ?? 0
    };
    const saved = await this.upsertByStore(TRAVEL_COLLECTIONS.services, storeCode, svc.id, payload);
    return { error: saved.error };
  }

  async deleteService(storeCode: string, id: string): Promise<{ error: string | null }> {
    return this.deleteByStore(TRAVEL_COLLECTIONS.services, storeCode, id);
  }

  async seedDefaultServices(storeCode: string): Promise<{ error: string | null }> {
    const defaults = [
      { name: 'Local trip', service_type: 'car_booking', sort_order: 1 },
      { name: 'Airport transfer', service_type: 'airport_transfer', sort_order: 2 },
      { name: 'Outstation', service_type: 'outstation', sort_order: 3 },
      { name: 'Car rental', service_type: 'car_rental', sort_order: 4 }
    ];
    for (const svc of defaults) {
      const { error } = await this.upsertService(storeCode, svc);
      if (error) return { error };
    }
    return { error: null };
  }

  // --- Drivers ---
  async listDrivers(storeCode: string): Promise<{ data: TravelDriverRow[]; error: string | null }> {
    return this.listByStore<TravelDriverRow>(TRAVEL_COLLECTIONS.drivers, storeCode);
  }

  async upsertDriver(
    storeCode: string,
    driver: Partial<TravelDriverRow> & { name: string; phone: string }
  ): Promise<{ error: string | null }> {
    const payload = {
      name: driver.name.trim(),
      phone: driver.phone.trim(),
      license_number: driver.license_number?.trim() || null,
      experience_years: driver.experience_years ?? null,
      driver_type: driver.driver_type || 'own_driver',
      is_available: driver.is_available !== false,
      status: driver.status || 'active'
    };
    const saved = await this.upsertByStore(TRAVEL_COLLECTIONS.drivers, storeCode, driver.id, payload);
    return { error: saved.error };
  }

  async deleteDriver(storeCode: string, id: string): Promise<{ error: string | null }> {
    return this.deleteByStore(TRAVEL_COLLECTIONS.drivers, storeCode, id);
  }

  // --- Customers ---
  async listCustomers(storeCode: string): Promise<{ data: TravelCustomerRow[]; error: string | null }> {
    return this.listByStore<TravelCustomerRow>(TRAVEL_COLLECTIONS.customers, storeCode);
  }

  async upsertCustomer(
    storeCode: string,
    customer: Partial<TravelCustomerRow> & { phone: string }
  ): Promise<{ error: string | null }> {
    const payload = {
      name: customer.name?.trim() || null,
      phone: customer.phone.trim(),
      email: customer.email?.trim() || null,
      address: customer.address?.trim() || null,
      city: customer.city?.trim() || null,
      village: customer.village?.trim() || null
    };
    const saved = await this.upsertByStore(
      TRAVEL_COLLECTIONS.customers,
      storeCode,
      customer.id,
      payload
    );
    return { error: saved.error };
  }

  // --- Bookings / rentals ---
  async submitDispatchBooking(input: { id: string; name: string; phone: string; pickup: string; drop: string }): Promise<string> {
    const phone = input.phone.replace(/[\s()+-]/g, '').replace(/^91(?=\d{10}$)/, '');
    if (!input.name.trim() || !/^\d{10}$/.test(phone) || !input.pickup.trim() || !input.drop.trim() || input.pickup.trim().toLowerCase() === input.drop.trim().toLowerCase()) {
      throw new Error('Enter your name, valid mobile number, and different pickup and drop locations.');
    }
    await setDoc(doc(this.db(), TRAVEL_COLLECTIONS.bookings, input.id), {
      storeCode: '', customer_name: input.name.trim(), customer_phone: phone, phoneDigits: phone,
      pickup_location: input.pickup.trim(), drop_location: input.drop.trim(),
      status: 'waiting_for_captain', booking_status: 'waiting_for_captain', source: 'ride-dispatch',
      createdAt: serverTimestamp(), updatedAt: serverTimestamp()
    });
    return input.id;
  }

  async assignBookingCaptain(bookingId: string, captainId: string): Promise<void> {
    if (!await this.rideAdmins.isCurrentUserRideAdmin()) throw new Error('Travel administrator access is required.');
    await runTransaction(this.db(), async tx => {
      const bookingRef = doc(this.db(), TRAVEL_COLLECTIONS.bookings, bookingId);
      const captainRef = doc(this.db(), 'captainProfiles', captainId);
      const booking = await tx.get(bookingRef);
      const captain = await tx.get(captainRef);
      if (!booking.exists() || !captain.exists()) throw new Error('Booking or captain no longer exists.');
      const row = booking.data();
      const profile = captain.data();
      if (row['booking_status'] !== 'waiting_for_captain') throw new Error('This booking has already changed. Refresh bookings.');
      if (profile['status'] !== 'active' || !profile['offersRide']) throw new Error('Choose an approved captain offering rides.');
      tx.update(bookingRef, { captain_id: captainId, driver_name: profile['name'], driver_phone: profile['phone'],
        vehicle_title: profile['vehicleTitle'], status: 'assigned', booking_status: 'assigned', updatedAt: serverTimestamp() });
    });
  }

  async listBookingsForAdmin(): Promise<TravelBookingRow[]> {
    if (!await this.rideAdmins.isCurrentUserRideAdmin()) {
      throw new Error('Travel administrator access is required.');
    }
    const snap = await getDocs(collection(this.db(), TRAVEL_COLLECTIONS.bookings));
    return snap.docs.map(row => this.mapStoreRow<TravelBookingRow>(row.id, row.data()))
      .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
  }

  async getBookingById(bookingId: string): Promise<TravelBookingRow | null> {
    const id = bookingId.trim();
    if (!id || id.includes('/')) return null;
    const snapshot = await getDoc(doc(this.db(), TRAVEL_COLLECTIONS.bookings, id));
    return snapshot.exists() ? this.mapStoreRow<TravelBookingRow>(id, snapshot.data()) : null;
  }

  async updateDispatchBookingStatus(id: string, expectedStatus: string, nextStatus: string): Promise<void> {
    if (!await this.rideAdmins.isCurrentUserRideAdmin()) throw new Error('Travel administrator access is required.');
    const transitions: Record<string, string[]> = {
      waiting_for_captain: ['cancelled'], assigned: ['started', 'cancelled'], started: ['completed', 'cancelled']
    };
    await runTransaction(this.db(), async tx => {
      const ref = doc(this.db(), TRAVEL_COLLECTIONS.bookings, id);
      const snapshot = await tx.get(ref);
      if (!snapshot.exists()) throw new Error('Booking no longer exists.');
      const row = snapshot.data();
      const current = row['booking_status'] || row['status'];
      if (row['source'] !== 'ride-dispatch') throw new Error('Manage this booking from its business page.');
      if (current !== expectedStatus) throw new Error('This booking has changed. Refresh bookings before trying again.');
      if (!transitions[current]?.includes(nextStatus)) throw new Error('This status change is not allowed.');
      if (nextStatus === 'started' && !row['captain_id']) throw new Error('Assign a captain before starting the ride.');
      tx.update(ref, { status: nextStatus, booking_status: nextStatus, updatedAt: serverTimestamp() });
    });
  }

  async listBookings(storeCode: string): Promise<{ data: TravelBookingRow[]; error: string | null }> {
    return this.listByStore<TravelBookingRow>(TRAVEL_COLLECTIONS.bookings, storeCode);
  }

  async updateBooking(
    storeCode: string,
    bookingId: string,
    patch: Partial<TravelBookingRow>
  ): Promise<{ error: string | null }> {
    try {
      const payload: Record<string, unknown> = { ...patch, updatedAt: serverTimestamp() };
      if (patch.booking_status) payload['status'] = patch.booking_status;
      await updateDoc(doc(this.db(), TRAVEL_COLLECTIONS.bookings, bookingId), payload);
      return { error: null };
    } catch (e) {
      return { error: e instanceof Error ? e.message : 'Update failed.' };
    }
  }

  async updateBookingStatus(
    storeCode: string,
    bookingId: string,
    status: string
  ): Promise<{ error: string | null }> {
    return this.updateBooking(storeCode, bookingId, { booking_status: status, status });
  }

  async listRentals(storeCode: string): Promise<{ data: TravelRentalRow[]; error: string | null }> {
    return this.listByStore<TravelRentalRow>(TRAVEL_COLLECTIONS.rentals, storeCode);
  }

  async updateRental(
    storeCode: string,
    rentalId: string,
    patch: Partial<TravelRentalRow>
  ): Promise<{ error: string | null }> {
    try {
      const payload: Record<string, unknown> = { ...patch, updatedAt: serverTimestamp() };
      if (patch.rental_status) payload['status'] = patch.rental_status;
      await updateDoc(doc(this.db(), TRAVEL_COLLECTIONS.rentals, rentalId), payload);
      return { error: null };
    } catch (e) {
      return { error: e instanceof Error ? e.message : 'Update failed.' };
    }
  }

  async updateRentalStatus(
    storeCode: string,
    rentalId: string,
    status: string
  ): Promise<{ error: string | null }> {
    return this.updateRental(storeCode, rentalId, { rental_status: status, status });
  }

  // --- Payments / expenses ---
  async listPayments(storeCode: string): Promise<{ data: TravelPaymentRow[]; error: string | null }> {
    return this.listByStore<TravelPaymentRow>(TRAVEL_COLLECTIONS.payments, storeCode);
  }

  async recordPayment(
    storeCode: string,
    payment: Partial<TravelPaymentRow> & { amount: number }
  ): Promise<{ error: string | null }> {
    const saved = await this.upsertByStore(TRAVEL_COLLECTIONS.payments, storeCode, undefined, {
      booking_id: payment.booking_id || null,
      rental_id: payment.rental_id || null,
      amount: payment.amount,
      payment_type: payment.payment_type || 'full',
      payment_mode: payment.payment_mode || 'cash',
      status: payment.status || 'success',
      transaction_id: payment.transaction_id?.trim() || null,
      paid_at: payment.paid_at || new Date().toISOString(),
      paidAt: serverTimestamp()
    });
    return { error: saved.error };
  }

  async listExpenses(storeCode: string): Promise<{ data: TravelExpenseRow[]; error: string | null }> {
    return this.listByStore<TravelExpenseRow>(TRAVEL_COLLECTIONS.expenses, storeCode);
  }

  async upsertExpense(
    storeCode: string,
    expense: Partial<TravelExpenseRow> & {
      expense_type: string;
      amount: number;
      expense_date: string;
    }
  ): Promise<{ error: string | null }> {
    const payload = {
      vehicle_id: expense.vehicle_id || null,
      expense_type: expense.expense_type,
      amount: expense.amount,
      description: expense.description?.trim() || null,
      expense_date: expense.expense_date
    };
    const saved = await this.upsertByStore(
      TRAVEL_COLLECTIONS.expenses,
      storeCode,
      expense.id,
      payload
    );
    return { error: saved.error };
  }

  async deleteExpense(storeCode: string, id: string): Promise<{ error: string | null }> {
    return this.deleteByStore(TRAVEL_COLLECTIONS.expenses, storeCode, id);
  }

  async getDashboardStats(
    storeCode: string
  ): Promise<{ data: TravelDashboardStats | null; error: string | null }> {
    try {
      const [vehicles, bookings, payments] = await Promise.all([
        this.listVehicles(storeCode),
        this.listBookings(storeCode),
        this.listPayments(storeCode)
      ]);
      const today = new Date().toISOString().slice(0, 10);
      const activeStatuses = new Set(['new', 'confirmed', 'assigned', 'started']);
      const monthPrefix = today.slice(0, 7);
      return {
        data: {
          total_vehicles: vehicles.data.length,
          available_vehicles: vehicles.data.filter((v) => v.is_active).length,
          active_bookings: bookings.data.filter((b) =>
            activeStatuses.has(String(b.booking_status || b.status || 'new'))
          ).length,
          today_bookings: bookings.data.filter((b) =>
            String(b.travel_date || b.created_at || '').startsWith(today)
          ).length,
          monthly_revenue: payments.data
            .filter((p) => String(p.paid_at || '').startsWith(monthPrefix))
            .reduce((sum, p) => sum + Number(p.amount || 0), 0),
          pending_payments: bookings.data.filter(
            (b) => String(b.payment_status || 'unpaid') !== 'paid'
          ).length
        },
        error: null
      };
    } catch (e) {
      return { data: null, error: e instanceof Error ? e.message : 'Stats failed.' };
    }
  }

  // --- Public storefront / book ride ---
  async getPublicStorefront(
    storeCode: string
  ): Promise<{ data: FirebasePublicTravelPayload | null; error: { message: string } | null }> {
    try {
      const store = await this.getStore(storeCode);
      if (!store || store.published === false) {
        return { data: null, error: { message: 'Travel storefront not found.' } };
      }
      const vehicles = await this.listVehicles(storeCode, true);
      const content = store.content || {};
      return {
        data: {
          content,
          store: {
            store_code: store.storeCode,
            store_name: store.storeName,
            store_type: 'travel',
            created_by: store.ownerUid
          },
          identity: {
            identity_id: null,
            store_type_sub: 'travel',
            logo_url: store.logoUrl || null,
            banner_url: null,
            display_name: store.storeName,
            contact_email: null,
            contact_phone: content.contact_phone || null,
            website_url: null,
            social_links: {},
            location: {},
            placeid: null
          },
          vehicles: vehicles.data.filter((v) => v.is_active),
          services: [],
          packages: content.packages || [],
          offers: content.offers || [],
          contact: {
            phone: content.contact_phone || null,
            whatsapp: content.contact_whatsapp || content.contact_phone || null
          }
        },
        error: null
      };
    } catch (e) {
      return {
        data: null,
        error: { message: e instanceof Error ? e.message : 'Failed to load travel storefront.' }
      };
    }
  }

  async submitBookingEnquiry(input: {
    storeCode: string;
    vehicleId?: string | null;
    customerName?: string;
    customerPhone: string;
    customerEmail?: string;
    pickupLocation?: string;
    dropLocation?: string;
    travelDate?: string;
    travelTime?: string;
    notes?: string;
    enquiryJson?: Record<string, unknown>;
  }): Promise<{ success: boolean; message?: string; id?: string }> {
    try {
      const phone = phoneDigits(input.customerPhone);
      if (phone.length < 10) return { success: false, message: 'Enter a valid phone number.' };
      const store = await this.getStore(input.storeCode);
      if (!store || store.published === false) {
        return { success: false, message: 'This ride service is unavailable.' };
      }
      let vehicleTitle: string | null = null;
      if (input.vehicleId) {
        const vehicles = await this.listVehicles(input.storeCode, true);
        vehicleTitle = vehicles.data.find((v) => v.id === input.vehicleId)?.title || null;
      }
      const id = newId();
      await setDoc(doc(this.db(), TRAVEL_COLLECTIONS.bookings, id), {
        storeCode: input.storeCode.trim(),
        vehicle_id: input.vehicleId || null,
        vehicle_title: vehicleTitle,
        customer_name: input.customerName?.trim() || null,
        customer_phone: phone,
        phoneDigits: phone,
        customer_email: input.customerEmail?.trim() || null,
        pickup_location: input.pickupLocation?.trim() || null,
        drop_location: input.dropLocation?.trim() || null,
        travel_date: input.travelDate || null,
        travel_time: input.travelTime || null,
        notes: input.notes?.trim() || null,
        enquiry_json: input.enquiryJson || {},
        status: 'new',
        booking_status: 'new',
        source: 'ride-storefront',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return { success: true, id };
    } catch (e) {
      return { success: false, message: e instanceof Error ? e.message : 'Booking failed.' };
    }
  }

  async submitRentalEnquiry(input: {
    storeCode: string;
    vehicleId?: string | null;
    customerName?: string;
    customerPhone: string;
    customerEmail?: string;
    pickupDate: string;
    returnDate: string;
    pickupLocation?: string;
    notes?: string;
    enquiryJson?: Record<string, unknown>;
  }): Promise<{ success: boolean; message?: string; id?: string; estimatedTotal?: number }> {
    try {
      const phone = phoneDigits(input.customerPhone);
      if (phone.length < 10) return { success: false, message: 'Enter a valid phone number.' };
      const store = await this.getStore(input.storeCode);
      if (!store || store.published === false) {
        return { success: false, message: 'This ride service is unavailable.' };
      }
      let vehicleTitle: string | null = null;
      let estimatedTotal: number | undefined;
      if (input.vehicleId) {
        const vehicles = await this.listVehicles(input.storeCode, true);
        const vehicle = vehicles.data.find((v) => v.id === input.vehicleId);
        vehicleTitle = vehicle?.title || null;
        estimatedTotal = vehicle?.daily_price_inr ?? undefined;
      }
      const id = newId();
      await setDoc(doc(this.db(), TRAVEL_COLLECTIONS.rentals, id), {
        storeCode: input.storeCode.trim(),
        vehicle_id: input.vehicleId || null,
        vehicle_title: vehicleTitle,
        customer_name: input.customerName?.trim() || null,
        customer_phone: phone,
        phoneDigits: phone,
        customer_email: input.customerEmail?.trim() || null,
        pickup_date: input.pickupDate,
        return_date: input.returnDate,
        pickup_location: input.pickupLocation?.trim() || null,
        notes: input.notes?.trim() || null,
        enquiry_json: input.enquiryJson || {},
        estimated_total_inr: estimatedTotal ?? null,
        status: 'new',
        rental_status: 'new',
        source: 'ride-storefront',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return { success: true, id, estimatedTotal };
    } catch (e) {
      return { success: false, message: e instanceof Error ? e.message : 'Rental request failed.' };
    }
  }

  async getActiveRide(input: {
    storeCode: string;
    customerPhone: string;
    bookingId?: string | null;
    rentalId?: string | null;
  }): Promise<{ data: FirebasePublicActiveRide | null; error: string | null }> {
    try {
      const phone = phoneDigits(input.customerPhone);
      if (phone.length < 10) {
        return { data: null, error: 'Store code and a 10-digit phone are required.' };
      }

      if (input.rentalId) {
        const snap = await getDoc(doc(this.db(), TRAVEL_COLLECTIONS.rentals, input.rentalId));
        if (snap.exists()) {
          const row = snap.data();
          if (row['storeCode'] === input.storeCode.trim() &&
            (row['phoneDigits'] === phone || row['customer_phone'] === phone)) {
            return { data: this.mapActiveRental(snap.id, row), error: null };
          }
        }
      }

      if (input.bookingId) {
        const snap = await getDoc(doc(this.db(), TRAVEL_COLLECTIONS.bookings, input.bookingId));
        if (snap.exists()) {
          const row = snap.data();
          if (row['storeCode'] === input.storeCode.trim() &&
            (row['phoneDigits'] === phone || row['customer_phone'] === phone)) {
            return { data: this.mapActiveBooking(snap.id, row), error: null };
          }
        }
      }

      // Guest tracking requires the saved booking/rental ID; collection queries are owner-only.
      return { data: null, error: null };
    } catch (e) {
      return { data: null, error: e instanceof Error ? e.message : 'Could not load ride.' };
    }
  }

  private mapActiveBooking(id: string, row: DocumentData): FirebasePublicActiveRide {
    return {
      kind: 'ride',
      id,
      status: String(row['status'] || row['booking_status'] || 'new'),
      vehicle_id: row['vehicle_id'] ? String(row['vehicle_id']) : null,
      vehicle_title: (row['vehicle_title'] as string) || null,
      pickup_location: (row['pickup_location'] as string) || null,
      drop_location: (row['drop_location'] as string) || null,
      travel_date: (row['travel_date'] as string) || null,
      travel_time: (row['travel_time'] as string) || null,
      fare_amount: row['fare_amount'] != null ? Number(row['fare_amount']) : null,
      driver_name: (row['driver_name'] as string) || null,
      driver_phone: (row['driver_phone'] as string) || null,
      created_at: asIso(row['createdAt']) || null
    };
  }

  private mapActiveRental(id: string, row: DocumentData): FirebasePublicActiveRide {
    return {
      kind: 'rental',
      id,
      status: String(row['status'] || row['rental_status'] || 'new'),
      vehicle_id: row['vehicle_id'] ? String(row['vehicle_id']) : null,
      vehicle_title: (row['vehicle_title'] as string) || null,
      pickup_location: (row['pickup_location'] as string) || null,
      pickup_date: (row['pickup_date'] as string) || null,
      return_date: (row['return_date'] as string) || null,
      estimated_total_inr:
        row['estimated_total_inr'] != null ? Number(row['estimated_total_inr']) : null,
      created_at: asIso(row['createdAt']) || null
    };
  }

  normalizeVehicle(raw: Record<string, unknown>): TravelVehicleRow {
    const urls = raw['image_urls'];
    return {
      id: String(raw['id'] || ''),
      store_code: String(raw['store_code'] || raw['storeCode'] || ''),
      sort_order: Number(raw['sort_order'] ?? 0),
      title: String(raw['title'] || ''),
      short_description: (raw['short_description'] as string) ?? null,
      image_url: (raw['image_url'] as string) ?? null,
      image_urls: Array.isArray(urls) ? urls.map(String) : [],
      vehicle_brand: (raw['vehicle_brand'] as string) ?? null,
      vehicle_type: (raw['vehicle_type'] as string) ?? null,
      manufacture_year: raw['manufacture_year'] != null ? Number(raw['manufacture_year']) : null,
      fuel_type: (raw['fuel_type'] as string) ?? null,
      transmission: (raw['transmission'] as string) ?? null,
      seating_capacity: raw['seating_capacity'] != null ? Number(raw['seating_capacity']) : null,
      daily_price_inr: raw['daily_price_inr'] != null ? Number(raw['daily_price_inr']) : null,
      supports_booking: raw['supports_booking'] !== false,
      supports_rental: raw['supports_rental'] !== false,
      extra_json: (raw['extra_json'] as Record<string, unknown>) || {},
      is_active: raw['is_active'] !== false,
      created_at: asIso(raw['createdAt'] || raw['created_at']),
      updated_at: asIso(raw['updatedAt'] || raw['updated_at'])
    };
  }
}
